// Utility-method to build the payload for normalization (expiration date, etc.)
// Add/remove fields in RENTAL_VERSION_NORMALIZE_FIELDS as needed for future backend logic
export const RENTAL_VERSION_NORMALIZE_FIELDS = [
  'id',
  'name',
  'scheduleAttributes',
  'createdAt',
  'deliveryType',
  'deliveryAddressStreetAddress1',
  'deliveryAddressStreetAddress2',
  'deliveryAddressCity',
  'deliveryAddressLocale',
  'deliveryAddressPostalCode',
  'deliveryAddressCountry',
  'customExpireDate',
  'extraTransportsAttributes',
];

// The extra delivery / pickup row fields sent to normalize (TD1-10271 §6.4): the API fields plus
// `rvId`, so that whatever echoes a row can only ever merge back onto the same store row (never
// append a copy); the read-only `label` never travels. `endDateTime` is the window end (D-9 amended).
export const EXTRA_TRANSPORT_PAYLOAD_FIELDS = ['rvId', 'id', 'transportType', 'startDateTime', 'endDateTime', '_destroy'];

const pickExtraTransportFields = (rows: unknown) =>
  Array.isArray(rows)
    ? rows.map((row) => {
        const picked: Record<string, unknown> = {};
        for (const key of EXTRA_TRANSPORT_PAYLOAD_FIELDS) {
          if (row?.[key] !== undefined) picked[key] = row[key];
        }
        return picked;
      })
    : rows;

// TD1-10516 (user ruling): "If we have any extra, the 'Add Delivery Times' in the UI is locked
// (cannot be unchecked) and the schedule object follows."
//
// The pre-epic invariant is "checkbox checked ⟺ `same_as_event_date === false`", and an order with
// extra legs holds that box checked and locked. So an order carrying extra legs must send
// `sameAsEventDate: false` — otherwise the API's `make_same_as_event_dates` overwrites the two
// order-owned windows from the event dates on save and silently discards what the user typed into
// the First Drop Off / Last Pick Up fields.
//
// Corrected here, on the way out, rather than written when the form loads: opening a form must not
// dirty a record. Orders that predate this (extras present, flag still true) therefore self-correct
// on their next save. Only ever narrows an existing `scheduleAttributes` — it never introduces one,
// since a schedule payload without its `id` would create a second Schedule rather than update the
// order's own.
const withScheduleSameAsEventDate = <T extends { extraTransportsAttributes?: unknown }>(
  data: T
): T => {
  const schedule = (data as { scheduleAttributes?: Record<string, unknown> }).scheduleAttributes;
  if (!schedule || typeof schedule !== 'object') return data;

  const rows = data.extraTransportsAttributes;
  const hasActiveExtraLeg =
    Array.isArray(rows) && rows.some((row) => row && row._destroy !== '1');
  if (!hasActiveExtraLeg || schedule.sameAsEventDate === false) return data;

  return { ...data, scheduleAttributes: { ...schedule, sameAsEventDate: false } };
};

// Every outbound payload that carries the form's extra delivery / pickup rows sends them through
// here, so `EXTRA_TRANSPORT_PAYLOAD_FIELDS` above is the single definition of what a row sends.
//
// It matters most for `label`: the labels are the API's to compute and it recomputes them on save
// (D-29), so a stored "Drop Off 3" travelling next to a freshly flipped `transport_type: pick_up`
// is a stale value contradicting the row it is attached to — a trap for the next reader at best,
// and something the API could act on at worst.
export const withExtraTransportPayloadRows = <T extends { extraTransportsAttributes?: unknown }>(
  data: T
): T => {
  if (data?.extraTransportsAttributes === undefined) return data;

  return withScheduleSameAsEventDate({
    ...data,
    extraTransportsAttributes: pickExtraTransportFields(data.extraTransportsAttributes),
  });
};

export function buildRentalVersionNormalizePayload(data: any) {
  const payload: any = {};
  for (const key of RENTAL_VERSION_NORMALIZE_FIELDS) {
    if (data[key] !== undefined) payload[key] = data[key];
  }
  if (payload.extraTransportsAttributes !== undefined) {
    payload.extraTransportsAttributes = pickExtraTransportFields(payload.extraTransportsAttributes);
  }
  return payload;
}
